import * as PIXI from "pixi.js";

import { ExpandedWildVisualizer } from "./components/ExpandedWildVisualizer";
import { EGameState, TSpinResponse } from "@src/server/ResponseTypes";
import { EventSystem } from "@src/core/events/EventSystem";
import { IView } from "@src/core/game/components/IView";
import { TGameUIButtonType } from "@src/game/ui/GameUI";
import { Symbol } from "@src/game/symbol-panel/Symbol";
import { gameEngine } from "src/game/GameEngine";
import { constants } from "src/game/Constants";
import { Spine } from "@pixi/spine-pixi";
import gsap from "gsap";
import { Audio } from "@src/core/audio/Audio";

// TODO: Rename to just Reel, and remove Symbol from the Reel event/met
// hod names since reel automatically implies a symbol reel

export class SymbolReel implements IView {
  public static readonly WORLD_Y_OFFSET: number = -120 as const;

  public static readonly WILD_INITIAL_STOP_DURATION_S: number = 0.3 as const; // 0.4
  public static readonly WILD_DELAY_DURATION_S: number = 0.1 as const; // 0.4

  public static readonly STOP_DELAY_S: number = 0.2 as const; // 0.3
  public static readonly STOP_DURATION_S: number = 0.5 as const;
  public static readonly SWIFT_STOP_DURATION_S: number = 0.1 as const;

  // Time in which the reel spins by the height of a single symbol.
  public static readonly SPIN_ITERATION_DURATION_S: number = 0.05 as const; // 0.05
  public static readonly SPIN_DURATION_INITIAL_S: number = 0.3 as const;
  public static readonly SPIN_DURATION_TOTAL_S: number = 0.3 as const;
  public static readonly SPIN_DELAY_S: number = 0.08 as const;

  public static readonly ANTICIPATION_SPIN_DURATION_S: number = 0.2 as const;
  public static readonly ANTICIPATION_SPIN_DURATION_TOTAL_S: number =
    5.5 as const;
  public static readonly ANTICIPATION_STOP_DURATION_S: number = 3.0 as const;

  public static readonly ONE_SYMBOL_SCROLL_TARGET_Y: number =
    Symbol.HEIGHT + SymbolReel.WORLD_Y_OFFSET;

  public static readonly FULL_SCROLL_TARGET_Y: number =
    Symbol.HEIGHT * gameEngine.config.rows + SymbolReel.WORLD_Y_OFFSET;

  private _view: PIXI.Container = new PIXI.Container();
  public get view(): PIXI.Container {
    return this._view;
  }

  // This stage is necessary to not be affected by masking
  // and stay in place, instead of moving with the reel.
  private _anticipationSkeletonView: PIXI.Container = new PIXI.Container();
  public get anticipationSkeletonView(): PIXI.Container {
    return this._anticipationSkeletonView;
  }

  private _expandedWildVisualizerView: PIXI.Container = new PIXI.Container();
  public get expandedWildVisualizerView(): PIXI.Container {
    return this._expandedWildVisualizerView;
  }

  // Since there can be 2 visualizers on the screen, we can have active one, which is
  // about to stop, and inactive one, which is scrolling off screen (play).
  private _inactiveExpandedWildVisualizer: ExpandedWildVisualizer | undefined =
    undefined;
  private _activeExpandedWildVisualizer: ExpandedWildVisualizer | undefined =
    undefined;

  private _index: number;
  private _symbols: Symbol[];
  private _isSlamStopping: boolean = false;
  // This property is only relevant before a spin is started.
  private _isSwiftButtonEnabled: boolean = false;
  private _completedSymbolWinAnimations: number = 0;

  private static _stoppedReelCount: number = 0;
  private static _gameActionDataReceivedReelCount: number = 0;
  private static _hasCoinSymbol: boolean = false;
  private static _scatterCount: number = 0;

  private _currentWinIndex: number = 0;
  private _anticipationSkeleton!: Spine;
  private _reelPlayAnimationTimeline!: GSAPTimeline;
  private _reelStopAnimationTimeline!: GSAPTimeline;

  private _boundOnWinningPopupClosed!: () => void;
  private _boundOnSpinStopped!: () => void;
  private _boundOnReelStopped!: (_: number) => void;
  private _boundOnReelStopRequested!: (_: number) => void;
  private _boundOnReelAnticipationSpinRequested!: (_: number) => void;
  private _boundOnReelAnticipationStopRequested!: (_: number) => void;
  private _boundOnPerformedGameAction!: (_: TSpinResponse) => void;
  private _boundOnSymbolPanelShown!: () => void;
  private _boundOnSymbolPanelHidden!: () => void;
  private _boundOnSymbolWinAnimationEnded!: (_: PIXI.Container) => void;
  private _boundOnScatterWinAnimationEnded!: (_: PIXI.Container) => void;
  private _boundOnCoinSplashStopped!: () => void;
  private _boundOnClickedInteractableArea!: () => void;
  private _boundOnClickedUIButton!: (_: TGameUIButtonType) => void;
  private _boundOnClickedRigButton!: (_: string) => void;
  private _boundOnClickedSwiftButton!: (_: boolean) => void;

  public constructor(reelIndex: number) {
    this._index = reelIndex;
    this._symbols = [];

    this._initializeAssets();
    this._parentAssets();

    this._createInitialSymbols();

    this._highlightSpecificWin = this._highlightSpecificWin.bind(this);
    this._onRequestedGameAction = this._onRequestedGameAction.bind(this);
    this._showNextWin = this._showNextWin.bind(this);

    this._boundOnWinningPopupClosed = this._onWinningPopupClosed.bind(this);
    this._boundOnSpinStopped = this._onSpinStopped.bind(this);
    this._boundOnReelStopped = this._onReelStopped.bind(this);
    this._boundOnReelStopRequested = this._onReelStopRequested.bind(this);
    this._boundOnReelAnticipationSpinRequested =
      this._onReelAnticipationSpinRequested.bind(this);
    this._boundOnReelAnticipationStopRequested =
      this._onReelAnticipationStopRequested.bind(this);
    this._boundOnPerformedGameAction = this._onPerformedGameAction.bind(this);
    this._boundOnSymbolPanelShown = this._onSymbolPanelShown.bind(this);
    this._boundOnSymbolPanelHidden = this._onSymbolPanelHidden.bind(this);
    this._boundOnSymbolWinAnimationEnded =
      this._onSymbolWinAnimationEnded.bind(this);
    this._boundOnScatterWinAnimationEnded =
      this._onScatterWinAnimationEnded.bind(this);
    this._boundOnCoinSplashStopped = this._onCoinSplashStopped.bind(this);
    this._boundOnClickedInteractableArea =
      this._onClickedInteractableArea.bind(this);
    this._boundOnClickedUIButton = this._onClickedUIButton.bind(this);
    this._boundOnClickedRigButton = this._onClickedRigButton.bind(this);
    this._boundOnClickedSwiftButton = this._onClickedSwiftButton.bind(this);

    // These two events do not need removal for the entire lifetime of the SymbolReel object.
    EventSystem.addListener(
      constants.GameEvents.SYMBOL_PANEL_SHOWN,
      this._boundOnSymbolPanelShown,
    );

    EventSystem.addListener(
      constants.GameEvents.SYMBOL_PANEL_HIDDEN,
      this._boundOnSymbolPanelHidden,
    );

    this._initializeReel();
  }

  private _showNextWin(): void {
    // If the game state has changed away from MAIN, stop the loop
    if (gameEngine.gameActionData.state !== EGameState.MAIN) {
      return;
    }

    const wins = gameEngine.gameActionData.wins;
    if (!wins || wins.length === 0 || this._index !== 0) return;

    EventSystem.invoke(constants.GameEvents.HIGHLIGHT_SPECIFIC_WIN, this._currentWinIndex);

    this._currentWinIndex = (this._currentWinIndex + 1) % wins.length;

    gsap.delayedCall(2.0, this._showNextWin);
  }

  private _initializeReel(): void {
    this._setupEventListeners();
  }

  private _resetReel(): void {
    SymbolReel._stoppedReelCount = 0;
    SymbolReel._gameActionDataReceivedReelCount = 0;
    SymbolReel._hasCoinSymbol = false;
    SymbolReel._scatterCount = 0;

    this._isSlamStopping = false;
    this._completedSymbolWinAnimations = 0;

    gsap.killTweensOf(this._showNextWin);

    this._reelPlayAnimationTimeline?.kill();
    this._reelStopAnimationTimeline?.kill();

    this._removeEventListeners();
  }

  private _initializeAssets(): void {
    this._anticipationSkeleton = Spine.from({
      skeleton: "anticAtlasJson",
      atlas: "anticAtlas",
    });
    this._anticipationSkeleton.zIndex = 1000;
    this._anticipationSkeleton.y = 270;
    this._anticipationSkeleton.scale.set(0.2, 0.25);
    this._anticipationSkeleton.visible = false;
  }

  private _parentAssets(): void {
    this._anticipationSkeletonView.addChild(this._anticipationSkeleton);
  }

  private _setupEventListeners(): void {


    EventSystem.addListener(
      constants.GameEvents.WINNING_POPUP_CLOSED,
      this._boundOnWinningPopupClosed,
    );

    EventSystem.addListener(
      constants.GameEvents.REEL_PANEL_SPIN_STOPPED,
      this._boundOnSpinStopped,
    );

    EventSystem.addListener(
      constants.GameEvents.SYMBOL_REEL_STOPPED,
      this._boundOnReelStopped,
    );

    EventSystem.addListener(
      constants.GameEvents.SYMBOL_REEL_STOP_REQUESTED,
      this._boundOnReelStopRequested,
    );

    EventSystem.addListener(
      constants.GameEvents.SYMBOL_REEL_ANTICIPATION_SPIN_REQUESTED,
      this._boundOnReelAnticipationSpinRequested,
    );

    EventSystem.addListener(
      constants.GameEvents.SYMBOL_REEL_ANTICIPATION_STOP_REQUESTED,
      this._boundOnReelAnticipationStopRequested,
    );

    EventSystem.addListener(
      constants.GameEvents.GAME_PERFORM_ACTION,
      this._boundOnPerformedGameAction,
    );

    EventSystem.addListener(
      constants.GameEvents.GAME_REQUEST_ACTION,
      this._onRequestedGameAction,
    );

    EventSystem.addListener(
      constants.GameEvents.SYMBOL_WIN_ANIMATION_ENDED,
      this._boundOnSymbolWinAnimationEnded,
    );

    EventSystem.addListener(
      constants.GameEvents.SYMBOL_WIN_ANIMATION_ENDED,
      this._boundOnScatterWinAnimationEnded,
    );

    EventSystem.addListener(
      constants.GameEvents.COIN_SPLASH_STOPPED,
      this._boundOnCoinSplashStopped,
    );

    EventSystem.addListener(
      constants.UIEvents.CLICKED_INTERACTABLE_AREA,
      this._boundOnClickedInteractableArea,
    );

    EventSystem.addListener(
      constants.UIEvents.CLICKED_UI_BUTTON,
      this._boundOnClickedUIButton,
    );

    EventSystem.addListener(
      constants.UIEvents.CLICKED_RIG_BUTTON,
      this._boundOnClickedRigButton,
    );

    EventSystem.addListener(
      constants.UIEvents.CLICKED_SWIFT_BUTTON,
      this._boundOnClickedSwiftButton,
    );

    EventSystem.addListener(
      constants.GameEvents.HIGHLIGHT_SPECIFIC_WIN,
      this._highlightSpecificWin
    );
  }

  private _removeEventListeners(): void {

    EventSystem.removeListener(
      constants.GameEvents.WINNING_POPUP_CLOSED,
      this._boundOnWinningPopupClosed,
    );

    EventSystem.removeListener(
      constants.GameEvents.REEL_PANEL_SPIN_STOPPED,
      this._boundOnSpinStopped,
    );

    EventSystem.removeListener(
      constants.GameEvents.SYMBOL_REEL_STOPPED,
      this._boundOnReelStopped,
    );

    EventSystem.removeListener(
      constants.GameEvents.SYMBOL_REEL_STOP_REQUESTED,
      this._boundOnReelStopRequested,
    );

    EventSystem.removeListener(
      constants.GameEvents.SYMBOL_REEL_ANTICIPATION_SPIN_REQUESTED,
      this._boundOnReelAnticipationSpinRequested,
    );

    EventSystem.removeListener(
      constants.GameEvents.SYMBOL_REEL_ANTICIPATION_STOP_REQUESTED,
      this._boundOnReelAnticipationStopRequested,
    );

    EventSystem.removeListener(
      constants.GameEvents.GAME_PERFORM_ACTION,
      this._boundOnPerformedGameAction,
    );

    EventSystem.removeListener(
      constants.GameEvents.GAME_REQUEST_ACTION,
      this._onRequestedGameAction,
    );

    EventSystem.removeListener(
      constants.GameEvents.SYMBOL_WIN_ANIMATION_ENDED,
      this._boundOnSymbolWinAnimationEnded,
    );

    EventSystem.removeListener(
      constants.GameEvents.SYMBOL_WIN_ANIMATION_ENDED,
      this._boundOnScatterWinAnimationEnded,
    );

    EventSystem.removeListener(
      constants.GameEvents.COIN_SPLASH_STOPPED,
      this._boundOnCoinSplashStopped,
    );

    EventSystem.removeListener(
      constants.UIEvents.CLICKED_INTERACTABLE_AREA,
      this._boundOnClickedInteractableArea,
    );

    EventSystem.removeListener(
      constants.UIEvents.CLICKED_UI_BUTTON,
      this._boundOnClickedUIButton,
    );

    EventSystem.removeListener(
      constants.UIEvents.CLICKED_RIG_BUTTON,
      this._boundOnClickedRigButton,
    );

    EventSystem.removeListener(
      constants.UIEvents.CLICKED_SWIFT_BUTTON,
      this._boundOnClickedSwiftButton,
    );

    EventSystem.removeListener(
      constants.GameEvents.HIGHLIGHT_SPECIFIC_WIN,
      this._highlightSpecificWin
    );
  }

  private _highlightSpecificWin(winIndex: number): void {
    this._setSymbolsDimmed(true);
    this._symbols.forEach(s => s.view.zIndex = constants.Layers.SYMBOL);

    // Only perform sequential highlighting if we are in MAIN state
    if (gameEngine.gameActionData.state !== EGameState.MAIN) return;

    const win = gameEngine.gameActionData.wins[winIndex];
    if (!win) return;

    // 1. Dim all symbols on this reel to prep for highlight
    this._setSymbolsDimmed(true);
    this._symbols.forEach(s => s.view.zIndex = constants.Layers.SYMBOL);

    // 2. Filter coordinates for this specific reel
    win.coordinates.forEach(coord => {
      if (coord.reel === this._index) {
        const symbol = this._symbols[coord.row];

        // 3. Highlight the specific symbol
        symbol.view.tint = 0xffffff;
        symbol.playWinAnimation();
        symbol.view.zIndex = constants.Layers.SYMBOL_HIGHLIGHTED;
      }
    });
  }

  private _onWinningPopupClosed(): void {
    this._completedSymbolWinAnimations = 0;

    switch (gameEngine.gameActionData.state) {
      case EGameState.MAIN:
        if (this._index === 0 && !gameEngine.isAutoplayActive) {
          this._currentWinIndex = 0;
          // gsap.delayedCall(0.01, () => );
          this._showNextWin()
        } break;
      case EGameState.MAIN_TO_HOLD:
      case EGameState.HOLD:
      case EGameState.BONUS_TO_HOLD:
        return;
    }

    this._highlightWinningScatters();
  }

  // We always wait for coin animations to finish before we can
  // declare the spin as stopped.
  private _onSpinStopped(): void {
    switch (gameEngine.gameActionData.state) {
      case EGameState.MAIN_TO_HOLD:
      case EGameState.HOLD:
      case EGameState.BONUS_TO_HOLD:
        return;
    }

    this._isSlamStopping = false;
    this._anticipationSkeleton.visible = false;

    this._highlightWinningSymbols();

    if (gameEngine.gameActionData.wins.length === 0) {
      this._highlightWinningScatters();
    }
  }

  private _onReelStopped(reelIndex: number): void {
    if (reelIndex !== this._index) return;

    SymbolReel._stoppedReelCount++;
    const hasScatter = this._symbols.some(s => s.type === "10");

    // 1. INDIVIDUAL REEL STOP (Normal Speed)
    if (!this._isSwiftButtonEnabled && !this._isSlamStopping) {
      Audio.play("sndStop");

      // Handle Wilds...
      if (!this._shouldShowExpandedWild(this._index)) {
        if (this._symbols.some(s => s.type === "1")) Audio.play("sndWildLand");
      } else {
        Audio.play(`sndWildLandExpand${this._index + 1}`);
      }

      // Handle Scatters incrementally
      if (hasScatter) {
        SymbolReel._scatterCount++;
        Audio.play(`sndScatterLand${SymbolReel._scatterCount}`);
        if (SymbolReel._scatterCount === 3) {
          Audio.play("sndFSTrigger");
          console.log("playing sndFSTrigger");
        }
      }
    }

    // 2. ALL REELS STOPPED (Panel Cleanup)
    if (SymbolReel._stoppedReelCount === gameEngine.config.reels) {
      Audio.stop("sndSpin");

      // SLAM / SWIFT STOP LOGIC
      if (this._isSwiftButtonEnabled || this._isSlamStopping) {
        Audio.play("sndStop");

        // A. Global Wild Logic (Check whole panel)
        const panelHasWild = gameEngine.gameActionData.panelNew.some(reel =>
          reel.some(type => type === "1")
        );
        if (panelHasWild) Audio.play("sndWildLand");

        // B. Global Scatter Logic (Check whole panel)
        // We count how many scatters total exist in the server data
        const totalScatters = gameEngine.gameActionData.scatters?.coordinates.length ?? 0;

        if (totalScatters > 0) {
          // Play the sound for the total count (e.g., 2 scatters = sndScatterLand2)
          Audio.play(`sndScatterLand${totalScatters}`);
          console.log(`Slam Stop: Playing scatter sound for total count: ${totalScatters}`);

          if (totalScatters === 3) {
            Audio.play("sndFSTrigger");
          }
        }
      }

      // --- Coin logic ---
      for (let i = 0; i < gameEngine.gameActionData.panelNew.length; ++i) {
        for (let j = 0; j < gameEngine.gameActionData.panelNew[i].length; ++j) {
          if (gameEngine.gameActionData.panelNew[i][j].startsWith("100")) {
            SymbolReel._hasCoinSymbol = true;
            break;
          }
        }
        if (SymbolReel._hasCoinSymbol) break;
      }

      if (SymbolReel._hasCoinSymbol) return;

      // --- Finalize Spin ---
      EventSystem.invoke(constants.GameEvents.REEL_PANEL_SPIN_STOPPED);

      SymbolReel._stoppedReelCount = 0;
      SymbolReel._gameActionDataReceivedReelCount = 0;
      SymbolReel._scatterCount = 0;
      this._isSlamStopping = false;
    }
  }

  // private _onReelSpinRequested(reelIndex: number): void {
  //   if (this._index === reelIndex) {
  //     this._queueSpinAnimationStart(reelIndex);
  //   }
  // }

  private _onReelStopRequested(reelIndex: number): void {
    if (this._index === reelIndex) {
      // TODO: Use queueSpionAnimationStop instead.
      this._stopSpinAnimation(reelIndex);
    }
  }

  private _onReelAnticipationSpinRequested(reelIndex: number): void {
    if (this._index === reelIndex) {
      this._playAnticipationSpinAnimation(reelIndex);
    }
  }

  private _onReelAnticipationStopRequested(reelIndex: number): void {
    if (this._index === reelIndex) {
      this._stopAnticipationSpinAnimation(reelIndex);
    }
  }

  private _onRequestedGameAction(): void {
    gsap.killTweensOf(this._showNextWin);
    this._setSymbolsDimmed(false);
    this._symbols.forEach(s => s.view.zIndex = constants.Layers.SYMBOL);

    this._queueSpin();

    this._isSlamStopping = false;
    this._anticipationSkeleton.visible = false;
    SymbolReel._gameActionDataReceivedReelCount = 0;
  }

  private _onPerformedGameAction(_: TSpinResponse): void {
    // ALL reels increment this so _isReadyToStop() works correctly
    SymbolReel._gameActionDataReceivedReelCount++;
  }

  private _onSymbolPanelShown(): void {
    this._initializeReel();
  }

  private _onSymbolPanelHidden(): void {
    this._resetReel();

    // When we quit the freespin mode, we must ensure
    // our wild symbols are back to being visible.
    // this._symbols.forEach((symbol) => (symbol.view.visible = true));
    // console.log(
    //   `active expanded visualizer ${this._activeExpandedWildVisualizer}, inactive expanded visualizer ${this._inactiveExpandedWildVisualizer}`,
    // );
    // if (this._activeExpandedWildVisualizer) {
    //   this._view.removeChild(this._activeExpandedWildVisualizer.view);
    // }
    // this._activeExpandedWildVisualizer = undefined;
    // if (this._inactiveExpandedWildVisualizer) {
    //   this._view.removeChild(this._inactiveExpandedWildVisualizer.view);
    // }
    // this._inactiveExpandedWildVisualizer = undefined;
  }

  private _onSymbolWinAnimationEnded(_: PIXI.Container): void {
    this._completedSymbolWinAnimations++;

    let expectedAnimations: number;
    if (
      this._isFullyWildReel(this._index) &&
      gameEngine.gameActionData.wins.length > 0
    ) {
      // Only the expanded wild plays animation, individual symbols are hidden.
      expectedAnimations = 1;
    } else {
      // Regular case - individual winning symbols.
      expectedAnimations = this._getWinningSymbolRows().length;
    }

    if (this._completedSymbolWinAnimations >= expectedAnimations) {
      if (gameEngine.gameActionData.state !== EGameState.MAIN) {
        this._setSymbolsDimmed(false);
        this._symbols.forEach(
          (symbol) => (symbol.view.zIndex = constants.Layers.SYMBOL),
        );
      }

      this._completedSymbolWinAnimations = 0;
    }
  }

  private _onScatterWinAnimationEnded(_: PIXI.Container): void {
    if (gameEngine.gameActionData.state !== EGameState.MAIN) {
      this._setSymbolsDimmed(false);
    }
    this._completedSymbolWinAnimations = 0;
  }

  private _onCoinSplashStopped(): void {
    if (SymbolReel._stoppedReelCount === gameEngine.config.reels) {
      EventSystem.invoke(constants.GameEvents.REEL_PANEL_SPIN_STOPPED);
      SymbolReel._hasCoinSymbol = false;
      SymbolReel._stoppedReelCount = 0;
      SymbolReel._gameActionDataReceivedReelCount = 0;
    }
  }

  private _onClickedInteractableArea(): void {
    if (this._isSlamStopping) {
      return;
    }

    this._isSlamStopping = true;
    this._anticipationSkeleton.state.setAnimation(0, "hide", false);

    Audio.stop("sndAnticipation");
    console.log("stopped anticipation soudmn");
  }

  private _onClickedUIButton(buttonType: TGameUIButtonType): void {
    switch (buttonType) {
      case "spin":
        break;
      // TODO: Add action button spinning later (add support in GameEngine.ts).
      // case "action":
      //   if (this._isSlamStopping) {
      //     return;
      //   }

      //   this._isSlamStopping = true;

      //   break;
    }
  }

  private _onClickedRigButton(_: string): void {
  }

  private _onClickedSwiftButton(enabled: boolean): void {
    this._isSwiftButtonEnabled = enabled;
  }

  private _queueSpinAnimationStart(reelIndex: number): void {
    gsap.delayedCall(SymbolReel.SPIN_DELAY_S * reelIndex, () => {
      this._playSpinAnimation(reelIndex);
    });
  }

  // TODO: Handle edge case when the player has to continue their paused game.
  private _queueSpin(): void {
    // if (gameEngine.gameActionData) {
    //     switch (gameEngine.gameActionData.state) {
    //         case EGameState.HOLD:
    //         case EGameState.HOLD_TO_BONUS:
    //         case EGameState.MAIN_TO_HOLD:
    //             return;
    //     }
    // }
    console.log("SYMBOL REEL QUEUED SPIN");

    this._isSlamStopping = false;

    this._queueSpinAnimationStart(this._index);
  }

  private _shouldStartAnticipationSpinAnimation(reelIndex: number): boolean {
    if (!gameEngine.gameActionData) {
      return false;
    }

    if (this._index === reelIndex) {
      const scatterCount: number =
        gameEngine.gameActionData.scatters?.coordinates.length ?? 0;


      return this._index === 3 && reelIndex === 3 && scatterCount === 3;
    }

    return false;
  }

  private _isReadyToStop(): boolean {
    console.log(
      `game action data received reel count ${SymbolReel._gameActionDataReceivedReelCount}`,
    );
    return (
      gameEngine.gameActionData !== undefined &&
      (
        SymbolReel._gameActionDataReceivedReelCount === gameEngine.config.reels
      )
    );
  }

  private _highlightWinningSymbols(): void {
    if (gameEngine.gameActionData.wins.length === 0) {
      return;
    }

    Audio.play("sndWinHighlight", false, 0.5);

    this._setSymbolsDimmed(true);


    this._getWinningSymbolRows().forEach((symbolRow: number) => {
      this._symbols[symbolRow].playWinAnimation();
      this._symbols[symbolRow].view.zIndex =
        constants.Layers.SYMBOL_HIGHLIGHTED;
    });


    if (this._isFullyWildReel(this._index)) {
      this._inactiveExpandedWildVisualizer?.playWinAnimation();
    }
  }

  private _highlightWinningScatters(): void {
    const scatterCount: number =
      gameEngine.gameActionData.scatters?.coordinates.length ?? 0;

    if (scatterCount < 3) {
      return;
    }

    this._setSymbolsDimmed(true);

    this._symbols.forEach((symbol) => {
      if (symbol.type === "10") {
        symbol.playWinAnimation();
      }
    });
  }

  private _setSymbolsDimmed(dimmed: boolean): void {
    const tint: number = dimmed ? 0x202020 : 0xffffff;

    this._symbols.forEach((symbol) => (symbol.view.tint = tint));

    if (this._activeExpandedWildVisualizer) {
      this._activeExpandedWildVisualizer.view.tint = tint;
    }
  }

  private _createInitialSymbols(): void {
    const initialSymbolTypes: string[] =
      gameEngine.gameInitialData.panel[this._index];

    for (let i = 0; i < initialSymbolTypes.length; i++) {
      const symbol: Symbol = Symbol.pool.acquire();
      symbol.setType(initialSymbolTypes[i].toString());
      symbol.view.y = this._getCenteredSymbolPositionY(i);
      this._view.addChild(symbol.view);

      this._symbols.push(symbol);
    }
  }

  private _getWinningSymbolRows(): number[] {
    let winningSymbolRows: number[] = [];

    for (let wId = 0; wId < gameEngine.gameActionData.wins.length; ++wId) {
      for (
        let cId = 0;
        cId < gameEngine.gameActionData.wins[wId].coordinates.length;
        ++cId
      ) {
        const coordinate = gameEngine.gameActionData.wins[wId].coordinates[cId];
        if (coordinate.reel === this._index) {
          const rowIndex = coordinate.row;

          if (!winningSymbolRows.includes(rowIndex)) {
            winningSymbolRows.push(rowIndex);
          }
        }
      }
    }

    return winningSymbolRows;
  }

  private _isFullyWildReel(reelIndex: number): boolean {
    if (this._index !== reelIndex) {
      return false;
    }

    let isFullyWildReel = true;

    const reel = gameEngine.gameActionData.panelNew[this._index];
    for (let i = 0; i < reel.length; i++) {
      if (reel[i] !== "1") {
        isFullyWildReel = false;
        break;
      }
    }

    return isFullyWildReel;
  }

  private _shouldShowExpandedWild(reelIndex: number): boolean {
    // gameActionData is basically guaranteed to be valid here
    if (gameEngine.gameActionData) {
      switch (gameEngine.gameActionData.state) {
        case EGameState.BONUS_TO_MAIN:
        case EGameState.BONUS:
          return this._isFullyWildReel(reelIndex);
      }
    }

    return false;
  }

  private _getCenteredSymbolPositionY(row: number): number {
    return Symbol.HEIGHT * row + Symbol.HEIGHT / 2;
  }

  private _getRandomRegularSymbolType(): string {
    return Math.ceil(Math.random() * 8 + 1).toString();
  }

  private _createNewSymbolAtTop(
    type: string = this._getRandomRegularSymbolType(),
  ): Symbol {
    const symbol: Symbol = Symbol.pool.acquire();
    symbol.setType(type);
    symbol.view.y = this._symbols[0].view.y - Symbol.HEIGHT;
    this._view.addChild(symbol.view);
    this._symbols.unshift(symbol);

    return symbol;
  }

  private _createFinalReelSymbolsAtTop(reelIndex: number): void {
    for (let i = gameEngine.config.rows - 1; i >= 0; --i) {
      const symbolType: string =
        gameEngine.gameActionData.panelNew[reelIndex][i];

      const symbol: Symbol = this._createNewSymbolAtTop(symbolType);

      const coinMultiplierText: string = symbolType.split("_")[1];
      symbol.setCoinMultiplierText(coinMultiplierText ?? "");
    }
  }

  private _releaseSymbolAtBottom(): void {
    const lastSymbol: Symbol = this._symbols.pop()!;
    Symbol.pool.release(lastSymbol);
    this._view.removeChild(lastSymbol.view);
  }

  private _releaseSymbolAtTop(): void {
    const firstSymbol: Symbol = this._symbols.shift()!;
    Symbol.pool.release(firstSymbol);
    this._view.removeChild(firstSymbol.view);
  }

  private _moveBottomSymbolToTop(): void {
    const lastSymbol: Symbol = this._symbols.pop()!;
    lastSymbol.setType(Math.ceil(Math.random() * 8 + 1).toString());
    this._symbols.unshift(lastSymbol);
    lastSymbol.view.y = -Symbol.HEIGHT / 2;
  }

  private _resetReelAndSymbolPositions(): void {
    this._view.y = SymbolReel.WORLD_Y_OFFSET;

    const lastIndex = this._symbols.length - 1;
    this._symbols[lastIndex].view.y = this._getCenteredSymbolPositionY(
      gameEngine.config.rows - 1,
    );

    for (let i = lastIndex - 1; i >= 0; --i) {
      this._symbols[i].view.y = this._symbols[i + 1].view.y - Symbol.HEIGHT;
    }
  }

  private _playSymbolsLandAnimations(): void {
    this._symbols.forEach((symbol) => symbol.playLandAnimation());
  }

  private _createActiveExpandedWildVisualizer(): void {
    this._activeExpandedWildVisualizer = new ExpandedWildVisualizer();
    this._expandedWildVisualizerView.addChild(
      this._activeExpandedWildVisualizer.view,
    );
  }

  private _isReadyToQuickstop(): boolean {
    return (
      (this._isSlamStopping || this._isSwiftButtonEnabled) &&
      this._isReadyToStop()
    );
  }

  private _playSpinAnimation(reelIndex: number): void {
    console.log("SYMBOL REEL PLAY ANIMATION STARTED");
    this._reelStopAnimationTimeline?.kill();
    this._reelPlayAnimationTimeline?.kill();
    this._reelPlayAnimationTimeline = gsap.timeline();

    let totalSpinDuration: number = 0;

    this._inactiveExpandedWildVisualizer?.playSpinAnimation();

    this._reelPlayAnimationTimeline
      .to(this._view, {
        y: SymbolReel.ONE_SYMBOL_SCROLL_TARGET_Y,
        duration: SymbolReel.SPIN_DURATION_INITIAL_S,
        ease: "back.in(3)",
        onStart: () => {
          this._createNewSymbolAtTop();
          this._symbols[0].view.visible = true;

          // if (this._isReadyToQuickstop()) {
          //   EventSystem.invoke(
          //     constants.GameEvents.SYMBOL_REEL_STOP_REQUESTED,
          //     reelIndex,
          //   );

          //   return;
          // }
        },
        onComplete: () => {
          this._symbols[this._symbols.length - 1].view.visible = true;
          this._moveBottomSymbolToTop();
          this._resetReelAndSymbolPositions();

          // NOTE: These are commented out because of desync issues.
          // Some Reels would stop here and some in the next tween's
          // onRepeat callback.

          // TODO: There are still desync issues. Investigate.

          // if (this._isReadyToQuickstop()) {
          //   console.log("stopped in complete method");

          //   EventSystem.invoke(
          //     constants.GameEvents.SYMBOL_REEL_STOP_REQUESTED,
          //     reelIndex,
          //   );

          //   return;
          // }
        },
      })
      .to(this._view, {
        y: SymbolReel.ONE_SYMBOL_SCROLL_TARGET_Y,
        duration: SymbolReel.SPIN_ITERATION_DURATION_S,
        ease: "none",
        repeat: -1,
        onRepeat: () => {
          // console.log(
          //   `stopped reel count ${ SymbolReel._stoppedReelCount }, data received reel count ${ SymbolReel._gameActionDataReceivedReelCount }`,
          // );
          this._symbols[0].view.visible = true;
          this._moveBottomSymbolToTop();
          this._resetReelAndSymbolPositions();

          if (this._isReadyToQuickstop()) {
            // console.log("stopped in repeat method");

            EventSystem.invoke(
              constants.GameEvents.SYMBOL_REEL_STOP_REQUESTED,
              reelIndex,
            );

            return;
          }

          const scatterCount: number =
            gameEngine.gameActionData?.scatters?.coordinates?.length ?? 0;

          const lastReelAnticipationDelay: number =
            this._index === 4 && reelIndex === 4 && scatterCount === 3
              ? SymbolReel.ANTICIPATION_SPIN_DURATION_TOTAL_S +
              SymbolReel.ANTICIPATION_STOP_DURATION_S
              : 0;

          // Add the anticipation spin duration to the 5th reel
          // if there are 3 scatters on the panel so it finishes
          // at the same time as the 4th reel.
          const maxDuration: number =
            SymbolReel.SPIN_DURATION_TOTAL_S +
            this._index * SymbolReel.STOP_DELAY_S +
            lastReelAnticipationDelay;

          totalSpinDuration += SymbolReel.SPIN_ITERATION_DURATION_S;

          const shouldStop: boolean = totalSpinDuration >= maxDuration;

          if (this._isReadyToStop() && shouldStop) {
            if (this._shouldStartAnticipationSpinAnimation(reelIndex)) {
              EventSystem.invoke(
                constants.GameEvents.SYMBOL_REEL_ANTICIPATION_SPIN_REQUESTED,
                reelIndex,
              );
              return;
            }

            EventSystem.invoke(
              constants.GameEvents.SYMBOL_REEL_STOP_REQUESTED,
              reelIndex,
            );
          }
        },
      });
  }

  private _stopSpinAnimation(reelIndex: number) {
    // Kill the decoy symbol created inside of the play method.
    // When directly calling the stop method, the decoy symbol doesnt exist.
    if (this._symbols.length > 4) {
      this._releaseSymbolAtTop();
    }

    this._reelPlayAnimationTimeline?.kill();
    this._reelStopAnimationTimeline?.kill();
    this._reelStopAnimationTimeline = gsap.timeline();

    this._createFinalReelSymbolsAtTop(reelIndex);

    if (!this._shouldShowExpandedWild(reelIndex)) {
      this._createStopAnimation(
        reelIndex,
        SymbolReel.FULL_SCROLL_TARGET_Y,
        this._isSlamStopping || this._isSwiftButtonEnabled
          ? SymbolReel.SWIFT_STOP_DURATION_S
          : SymbolReel.STOP_DURATION_S,
      );

      if (this._isSwiftButtonEnabled || this._isSlamStopping) {
        this._inactiveExpandedWildVisualizer?.playSwiftStopAnimation();
      }

      return;
    }


    // Hide the four singular wild symbols and let the expanded overlay shine.
    for (let i = 0; i < gameEngine.config.rows; ++i) {
      this._symbols[i].view.visible = false;
    }
    this._createActiveExpandedWildVisualizer();


    if (this._isSlamStopping || this._isSwiftButtonEnabled) {
      this._activeExpandedWildVisualizer!.playSwiftSpinAnimation();
      this._inactiveExpandedWildVisualizer?.playSwiftStopAnimation();

      this._createStopAnimation(
        reelIndex,
        SymbolReel.FULL_SCROLL_TARGET_Y,
        SymbolReel.SWIFT_STOP_DURATION_S,
      );

      return;
    }

    // If the reel is fully wild, move it down by one symbol, wait,
    // then continue moving the rest of the symbols down. I.e split into 1:3 ratio tweens.
    let secondaryStopDelay = SymbolReel.WILD_DELAY_DURATION_S;
    const secondaryScrollDuration: number =
      (SymbolReel.STOP_DURATION_S / gameEngine.config.rows) *
      (gameEngine.config.rows - 1);

    this._reelStopAnimationTimeline
      .to(this._view, {
        y: SymbolReel.ONE_SYMBOL_SCROLL_TARGET_Y,
        duration: SymbolReel.WILD_INITIAL_STOP_DURATION_S,
        ease: "back.out(0.4)",
        onStart: () => {
          this._activeExpandedWildVisualizer!.playStopAnimation(
            secondaryScrollDuration,
          );
        },
        onComplete: () => {
          if (this._isSlamStopping || this._isSwiftButtonEnabled) {
            secondaryStopDelay = 0;
          }
        },
      })
      .to(
        {},
        {
          duration: secondaryStopDelay / 10,
          repeat: 10,
          onStart: () => {
            if (this._isSlamStopping || this._isSwiftButtonEnabled) {
              this._reelStopAnimationTimeline?.kill();

              this._inactiveExpandedWildVisualizer?.playSwiftStopAnimation();
              this._activeExpandedWildVisualizer!.playSwiftSpinAnimation();

              this._createStopAnimation(
                reelIndex,
                SymbolReel.FULL_SCROLL_TARGET_Y,
                SymbolReel.SWIFT_STOP_DURATION_S,
              );
              return;
            }
          },
          onRepeat: () => {
            // Poll for slam stop during expanded wild delay 10 times in the specified delay.
            if (this._isSlamStopping || this._isSwiftButtonEnabled) {
              this._reelStopAnimationTimeline?.kill();

              this._inactiveExpandedWildVisualizer?.playSwiftStopAnimation();
              this._activeExpandedWildVisualizer!.playSwiftSpinAnimation();

              this._createStopAnimation(
                reelIndex,
                SymbolReel.FULL_SCROLL_TARGET_Y,
                SymbolReel.SWIFT_STOP_DURATION_S,
              );
              return;
            }
          },
          onComplete: () => {
            console.log("SYMBOL REEL PLAY ANIMATION STOPPED");
            this._createStopAnimation(
              reelIndex,
              SymbolReel.FULL_SCROLL_TARGET_Y,
              secondaryScrollDuration,
            );
          },
        },
      );
  }

  private _createStopAnimation(
    reelIndex: number,
    offsetY: number,
    duration: number,
  ): void {
    this._reelStopAnimationTimeline?.kill();
    this._reelStopAnimationTimeline = gsap.timeline();

    this._reelStopAnimationTimeline.to(this._view, {
      y: offsetY,
      duration: duration,
      ease: "quad.out(0.4)",
      onComplete: () => {
        // Release the same count of symbols we created.
        for (let i = 0; i < gameEngine.config.rows; ++i) {
          this._releaseSymbolAtBottom();
        }

        this._resetReelAndSymbolPositions();
        this._playSymbolsLandAnimations();
        this._inactiveExpandedWildVisualizer =
          this._activeExpandedWildVisualizer;
        this._activeExpandedWildVisualizer = undefined;

        EventSystem.invoke(constants.GameEvents.SYMBOL_REEL_STOPPED, reelIndex);

        this._reelStopAnimationTimeline?.kill();

        // console.log(
        //   "SYMBOLS AFTER REMOVING THE DECOY SYMBOLS",
        //   this._symbols.length,
        //   JSON.stringify(this._symbols.map((s) => s.type)),
        // );
      },
    });
  }

  private _playAnticipationSpinAnimation(reelIndex: number) {
    Audio.play("sndAnticipation");
    this._reelStopAnimationTimeline?.kill();
    this._reelPlayAnimationTimeline?.kill();
    this._reelPlayAnimationTimeline = gsap.timeline();

    let totalSpinDuration: number = 0;

    this._reelPlayAnimationTimeline.to(this._view, {
      y: SymbolReel.ONE_SYMBOL_SCROLL_TARGET_Y,
      duration: SymbolReel.ANTICIPATION_SPIN_DURATION_S,
      ease: "none",
      repeat: -1,
      onStart: () => {
        this._anticipationSkeleton.visible = true;
        this._anticipationSkeleton.state.setAnimation(0, "show", false);
        this._anticipationSkeleton.state.addAnimation(0, "idle", true);
        // console.log(`playing anticipation animation for reel ${ reelIndex }`);
      },
      onRepeat: () => {
        if (
          (this._isSlamStopping || this._isSwiftButtonEnabled) &&
          this._isReadyToStop()
        ) {
          EventSystem.invoke(
            constants.GameEvents.SYMBOL_REEL_STOP_REQUESTED,
            reelIndex,
          );

          return;
        }

        const maxDuration: number =
          SymbolReel.ANTICIPATION_SPIN_DURATION_TOTAL_S +
          this._index * SymbolReel.STOP_DELAY_S;

        totalSpinDuration += SymbolReel.ANTICIPATION_SPIN_DURATION_S;

        const shouldStop: boolean = totalSpinDuration >= maxDuration;

        if (this._isReadyToStop() && shouldStop) {
          this._moveBottomSymbolToTop();
          this._resetReelAndSymbolPositions();

          this._reelPlayAnimationTimeline?.kill()

          EventSystem.invoke(
            constants.GameEvents.SYMBOL_REEL_ANTICIPATION_STOP_REQUESTED,
            reelIndex,
          );

          return;
        }

        if (!shouldStop) {
          this._moveBottomSymbolToTop();
          this._resetReelAndSymbolPositions();
        }
      },
    });
  }

  private _stopAnticipationSpinAnimation(reelIndex: number) {
    // Kill the decoy symbol created inside of the play method.
    // When directly calling the stop method, the decoy symbol doesnt exist.
    if (this._symbols.length > 4) {
      this._releaseSymbolAtTop();
    }

    console.log("anda");

    this._reelPlayAnimationTimeline?.kill();
    this._reelStopAnimationTimeline?.kill();
    this._reelStopAnimationTimeline = gsap.timeline();

    this._createFinalReelSymbolsAtTop(reelIndex);

    this._reelStopAnimationTimeline.to(this._view, {
      y: SymbolReel.FULL_SCROLL_TARGET_Y,
      duration: SymbolReel.ANTICIPATION_STOP_DURATION_S,
      ease: "back.out(0.4)",
      onComplete: () => {
        // Release the same count of symbols we created.
        for (let i = 0; i < gameEngine.config.rows; ++i) {
          this._releaseSymbolAtBottom();
        }

        this._resetReelAndSymbolPositions();
        this._playSymbolsLandAnimations();

        this._anticipationSkeleton.state.setAnimation(0, "hide", false);

        EventSystem.invoke(constants.GameEvents.SYMBOL_REEL_STOPPED, reelIndex);

        // console.log(
        //   "SYMBOLS AFTER REMOVING THE DECOY SYMBOLS",
        //   this._symbols.length,
        //   JSON.stringify(this._symbols.map((s) => s.type)),
        // );
      },
    });
  }
}
